import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import {
  ProsodyValues,
  defaultProsodyValues,
} from "shared/models/tts-editor/prosody";

interface VoiceAttributes {
  name?: string;
  language?: string;
}

interface StyleAttributes {
  name?: string;
  intensity?: string;
}

interface LanguageAttributes {
  language?: string;
  accent?: string;
}

type PreviewType = "voice" | "style" | "prosody" | "language";

interface SSMLPreviewProps {
  type: PreviewType;
  voiceAttributes?: VoiceAttributes;
  styleAttributes?: StyleAttributes;
  prosodyValues?: ProsodyValues;
  languageAttributes?: LanguageAttributes;
}

export default function SSMLPreview({
  type,
  voiceAttributes = {},
  styleAttributes = {},
  prosodyValues = { ...defaultProsodyValues },
  languageAttributes = {},
}: SSMLPreviewProps) {
  const getVoicePreviewText = () => {
    const { name, language } = voiceAttributes;
    if (!name && !language) return "<voice> </voice>";
    const attributes = [];
    if (name) attributes.push(`name="${name}"`);
    if (language) attributes.push(`language="${language.toLowerCase()}"`);
    return `<voice ${attributes.join(" ")}> </voice>`;
  };

  const getStylePreviewText = () => {
    const { name, intensity } = styleAttributes;
    if (!name && !intensity) return "<voice> </voice>";
    const attributes = [];
    if (name) attributes.push(`name="${name}"`);
    if (intensity) attributes.push(`intensity="${intensity.toLowerCase()}"`);
    return `<style ${attributes.join(" ")}> </style>`;
  };

  const getProsodyPreviewText = () => {
    // Create a copy of prosody values to modify
    const filteredValues = { ...prosodyValues };

    // Implement mutual exclusivity logic
    if (filteredValues.rate !== "") {
      filteredValues.duration = "";
    }
    if (filteredValues.pitch !== "") {
      filteredValues.contour = "";
    }

    // Filter empty values and join the attributes with values
    const activeAttributes = Object.entries(filteredValues)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== "")
      .map(([key, value]) => `${key}="${value}"`);

    return activeAttributes.length === 0
      ? "<prosody> </prosody>"
      : `<prosody ${activeAttributes.join(" ")}> </prosody>`;
  };

  const getLanguagePreviewText = () => {
    const { language, accent } = languageAttributes;
    if (!language) return "<lang> </lang>";
    const attributes = [];
    attributes.push(`language="${language.toLowerCase()}"`);
    if (accent && accent !== "none") attributes.push(`accent="${accent}"`);
    return `<lang ${attributes.join(" ")}> </lang>`;
  };

  const getPreviewText = () => {
    switch (type) {
      case "voice":
        return getVoicePreviewText();
      case "style":
        return getStylePreviewText();
      case "prosody":
        return getProsodyPreviewText();
      case "language":
        return getLanguagePreviewText();
      default:
        return "";
    }
  };

  return (
    <Box sx={{ color: "text.secondary" }}>
      <Typography variant="overline" fontFamily="monospace">
        {getPreviewText()}
      </Typography>
    </Box>
  );
}
